import "./Modal.scss";
import {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  useEffect,
} from "react";

const Modal = forwardRef((props, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasMeasured, setHasMeasured] = useState(false);
  const [content, setContent] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [computedArrow, setComputedArrow] = useState(null);
  const [requestedArrow, setRequestedArrow] = useState(null);

  const modalRef = useRef(null);
  const triggerRef = useRef(null);
  const lastTriggerRef = useRef(null);
  const isVisibleRef = useRef(false);
  const onCloseCallbackRef = useRef(null);

  useImperativeHandle(ref, () => ({
    open({ arrow, content, triggerElement, modalType, onClose }) {
      // If the same trigger is clicked and modal is visible, close it (toggle)
      if (isVisibleRef.current && lastTriggerRef.current === triggerElement) {
        setIsVisible(false);
        isVisibleRef.current = false;
        if (onCloseCallbackRef.current) {
          onCloseCallbackRef.current();
        }
        return;
      }

      // Dispatch global event to notify other modals to close
      window.dispatchEvent(
        new CustomEvent("modal-opened", {
          detail: { modalType: modalType || "default" },
        })
      );

      setRequestedArrow(arrow);
      setContent(content);
      triggerRef.current = triggerElement;
      lastTriggerRef.current = triggerElement;
      onCloseCallbackRef.current = onClose || null;
      setIsVisible(true);
      isVisibleRef.current = true;
      setHasMeasured(false);
    },
    close(skipCallback = false) {
      setIsVisible(false);
      isVisibleRef.current = false;
      if (!skipCallback && onCloseCallbackRef.current) {
        onCloseCallbackRef.current();
        onCloseCallbackRef.current = null;
      }
    },
  }));

  useEffect(() => {
    // Allow positioning for center-center modals without a trigger element
    if (!isVisible || !modalRef.current) return;

    // Use requestAnimationFrame to ensure DOM has updated
    const positionModal = () => {
      if (!modalRef.current) return;

      const modalRect = modalRef.current.getBoundingClientRect();

      // Determine the actual arrow position (handle auto- prefix)
      let actualArrow = requestedArrow;
      let triggerRect = null;

      if (triggerRef.current) {
        triggerRect = triggerRef.current.getBoundingClientRect();
      }

      if (requestedArrow && requestedArrow.startsWith("auto-") && triggerRect) {
        const basePosition = requestedArrow.replace("auto-", "");

        // Calculate if modal would fit in the preferred direction
        if (basePosition.startsWith("top-")) {
          // For top positioning, modal appears below trigger
          // Check if there's enough space below
          const spaceBelow = window.innerHeight - triggerRect.bottom;
          const spaceAbove = triggerRect.top;

          // If not enough space below but more space above, flip to bottom
          if (spaceBelow < modalRect.height + 20 && spaceAbove > spaceBelow) {
            actualArrow = basePosition.replace("top-", "bottom-");
          } else {
            actualArrow = basePosition;
          }
        } else if (basePosition.startsWith("bottom-")) {
          // For bottom positioning, modal appears above trigger
          // Check if there's enough space above
          const spaceAbove = triggerRect.top;
          const spaceBelow = window.innerHeight - triggerRect.bottom;

          // If not enough space above but more space below, flip to top
          if (spaceAbove < modalRect.height + 20 && spaceBelow > spaceAbove) {
            actualArrow = basePosition.replace("bottom-", "top-");
          } else {
            actualArrow = basePosition;
          }
        } else {
          actualArrow = basePosition;
        }
      }

      setComputedArrow(actualArrow);

      const needsTrigger = actualArrow && actualArrow !== "center-center";
      const hasRequiredRefs = !needsTrigger || triggerRect;

      if (hasRequiredRefs) {
        let top = 0;
        let left = 0;

        // For center-center or when no trigger exists, position at viewport center
        if (actualArrow === "center-center" || !triggerRect) {
          top = window.innerHeight / 2 - modalRect.height / 2;
          left = window.innerWidth / 2 - modalRect.width / 2;
        } else {
          // For all other arrow positions, calculate relative to trigger
          switch (actualArrow) {
            case "top-left":
              top = triggerRect.bottom + 8;
              left = triggerRect.left + triggerRect.width / 2 - 28;
              break;
            case "top-center":
              top = triggerRect.bottom + 8;
              left =
                triggerRect.left + triggerRect.width / 2 - modalRect.width / 2;
              break;
            case "top-right":
              top = triggerRect.bottom + 8;
              left = triggerRect.right - modalRect.width;
              break;

            case "bottom-left":
              top = triggerRect.top - modalRect.height - 8;
              left = triggerRect.left + triggerRect.width / 2 - 28;
              break;
            case "bottom-center":
              top = triggerRect.top - modalRect.height - 8;
              left =
                triggerRect.left + triggerRect.width / 2 - modalRect.width / 2;
              break;
            case "bottom-right":
              top = triggerRect.top - modalRect.height - 8;
              left = triggerRect.right - triggerRect.width / 2 - 28;
              break;

            case "left-top":
              top =
                triggerRect.top -
                modalRect.height +
                triggerRect.height / 2 +
                28;
              left = triggerRect.left + triggerRect.width + 8;
              break;
            case "left-center":
              top =
                triggerRect.top - modalRect.height / 2 + triggerRect.height / 2;
              left = triggerRect.left + triggerRect.width + 8;
              break;
            case "left-bottom":
              top = triggerRect.top + triggerRect.height / 2 - 28;
              left = triggerRect.left + triggerRect.width + 8;
              break;

            case "right-top":
              top =
                triggerRect.top -
                modalRect.height +
                triggerRect.height / 2 +
                28;
              left = triggerRect.left - modalRect.width - 8;
              break;
            case "right-center":
              top =
                triggerRect.top - modalRect.height / 2 + triggerRect.height / 2;
              left = triggerRect.left - modalRect.width - 8;
              break;
            case "right-bottom":
              top = triggerRect.top + triggerRect.height / 2 - 28;
              left = triggerRect.left - modalRect.width - 8;
              break;

            default:
              top = window.innerHeight / 2 - modalRect.height / 2;
              left = window.innerWidth / 2 - modalRect.width / 2;
              break;
          }
        }

        // Ensure modal stays within viewport bounds
        const minTop = 10; // Minimum distance from top of viewport
        const maxTop = window.innerHeight - modalRect.height - 10; // Maximum to prevent bottom overflow

        if (top < minTop) {
          top = minTop;
        } else if (top > maxTop) {
          top = maxTop;
        }

        setPosition({ top, left });

        // Set hasMeasured after a small delay to allow content to load
        if (!hasMeasured) {
          setHasMeasured(true);
        }
      }
    };

    // Delay positioning slightly to allow content to render
    const timeoutId = setTimeout(positionModal, 50);

    return () => clearTimeout(timeoutId);
  }, [isVisible, requestedArrow]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        // Only check trigger if it exists
        if (triggerRef.current && triggerRef.current.contains(event.target)) {
          return; // Don't close if clicking on trigger
        }
        setIsVisible(false);
        isVisibleRef.current = false;
        if (onCloseCallbackRef.current) {
          onCloseCallbackRef.current();
          onCloseCallbackRef.current = null;
        }
      }
    };

    const handleScroll = (event) => {
      // Only close if scroll is happening outside the modal
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsVisible(false);
        isVisibleRef.current = false;
        if (onCloseCallbackRef.current) {
          onCloseCallbackRef.current();
          onCloseCallbackRef.current = null;
        }
      }
    };

    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true); // Use capture phase to catch all scroll events
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div
      className={`modal ${computedArrow || ""}`}
      ref={modalRef}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        visibility: hasMeasured ? "visible" : "hidden",
        position: "fixed",
      }}
    >
      <div className="modal-content">{content}</div>
    </div>
  );
});

export default Modal;
